import React, { useState, useEffect } from 'react';
import { FormSubmission } from '../types';
import { adminFormsService } from '../services/admin-forms.service';
import { ScoreBreakdownDisplay } from './ScoreBreakdownDisplay';
import { COLORS } from '../../../../core/constants/colors';

interface SubmissionDrawerProps {
  submission: FormSubmission | null;
  submissionId: number | null;
  isOpen: boolean;
  onClose: () => void;
}

export const SubmissionDrawer: React.FC<SubmissionDrawerProps> = ({
  submission: submissionProp,
  submissionId,
  isOpen,
  onClose,
}) => {
  const [submission, setSubmission] = useState<FormSubmission | null>(submissionProp || null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (submissionProp) {
        setSubmission(submissionProp);
      } else if (submissionId) {
        loadSubmission();
      }
    } else {
      setSubmission(null);
      setError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, submissionId, submissionProp]);

  const loadSubmission = async () => {
    if (!submissionId) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await adminFormsService.getSubmissionById(submissionId);
      if (data) {
        setSubmission(data);
      } else {
        setError('Submission not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load submission');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Side Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-3xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold" style={{ color: '#1F2937' }}>
              Submission Details
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 py-6 overflow-y-auto">
            {isLoading && (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent mb-2"
                  style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
                />
                <p className="text-sm" style={{ color: '#6B7280' }}>Loading submission...</p>
              </div>
            )}

            {error && (
              <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
                <p className="text-sm" style={{ color: '#DC2626' }}>{error}</p>
              </div>
            )}

            {submission && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Submission ID
                    </label>
                    <p className="text-base font-semibold" style={{ color: '#1F2937' }}>
                      #{submission.id}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Company ID
                    </label>
                    <p className="text-base" style={{ color: '#1F2937' }}>
                      {submission.company_id}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Form Schema ID
                    </label>
                    <p className="text-base" style={{ color: '#1F2937' }}>
                      {submission.form_schema_id}
                    </p>
                  </div>
                  {submission.submitted_by && (
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                        Submitted By
                      </label>
                      <p className="text-base" style={{ color: '#1F2937' }}>
                        {submission.submitted_by}
                      </p>
                    </div>
                  )}
                </div>

                {(submission.completeness_score !== undefined || 
                  (submission.score_breakdown && submission.score_breakdown.overall_score_percent !== undefined)) && (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Completeness Score
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-gray-200 rounded-full h-4">
                        <div
                          className="h-4 rounded-full transition-all"
                          style={{
                            width: `${submission.completeness_score ?? submission.score_breakdown?.overall_score_percent ?? 0}%`,
                            backgroundColor: (submission.completeness_score ?? submission.score_breakdown?.overall_score_percent ?? 0) >= 80 ? '#4CAF50' : (submission.completeness_score ?? submission.score_breakdown?.overall_score_percent ?? 0) >= 50 ? '#FF9800' : '#F44336',
                          }}
                        />
                      </div>
                      <span className="text-base font-semibold" style={{ color: '#1F2937' }}>
                        {submission.completeness_score ?? submission.score_breakdown?.overall_score_percent?.toFixed(1) ?? 0}%
                      </span>
                    </div>
                  </div>
                )}

                {submission.score_breakdown && Object.keys(submission.score_breakdown).length > 0 && (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Score Breakdown
                    </label>
                    <ScoreBreakdownDisplay scoreBreakdown={submission.score_breakdown} />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                    Submission Data
                  </label>
                  <div className="border rounded-lg p-4 max-h-96 overflow-y-auto" style={{ borderColor: '#E5E7EB' }}>
                    {Object.keys(submission.submission_data).length === 0 ? (
                      <p className="text-sm" style={{ color: '#9CA3AF' }}>No submission data</p>
                    ) : (
                      <div className="space-y-3">
                        {Object.entries(submission.submission_data).map(([key, value]) => (
                          <div
                            key={key}
                            className="p-3 rounded-lg bg-gray-50"
                          >
                            <label className="text-xs font-medium uppercase mb-1 block" style={{ color: '#6B7280' }}>
                              {key}
                            </label>
                            <p className="text-sm" style={{ color: '#1F2937' }}>
                              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t" style={{ borderColor: '#E5E7EB' }}>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Submitted At
                    </label>
                    <p className="text-base" style={{ color: '#6B7280' }}>
                      {new Date(submission.submitted_at).toLocaleString()}
                    </p>
                  </div>
                  {submission.last_scored_at && (
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                        Last Scored At
                      </label>
                      <p className="text-base" style={{ color: '#6B7280' }}>
                        {new Date(submission.last_scored_at).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

